import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";

const API = 'http://localhost:8000/api/tools';

// ── ICONS ─────────────────────────────────────────────────────────────
const Icon = ({ name, size = 18 }) => {
  const icons = {
    globe:   <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
    search:  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
    server:  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>,
    shield:  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
    zap:     <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
    route:   <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="6" cy="19" r="3"/><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15"/><circle cx="18" cy="5" r="3"/></svg>,
    wifi:    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/></svg>,
    copy:    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>,
    check:   <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
    alert:   <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
    loader:  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/></svg>,
    port:    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
  };
  return icons[name] || null;
};

// ── SHARED COMPONENTS ─────────────────────────────────────────────────
function Card({ children, style = {} }) {
  return (
    <div style={{
      background: 'var(--surface)',
      border: '0.5px solid var(--border)',
      borderRadius: 14,
      overflow: 'hidden',
      ...style,
    }}>{children}</div>
  );
}

function CardHead({ icon, title, subtitle }) {
  return (
    <div style={{
      padding: '16px 20px',
      borderBottom: '0.5px solid var(--border)',
      display: 'flex', alignItems: 'center', gap: 10,
      background: 'var(--surface2)',
    }}>
      <div style={{
        width: 34, height: 34, borderRadius: 9,
        background: 'rgba(53,187,234,.15)',
        border: '0.5px solid rgba(53,187,234,.3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--blue)', flexShrink: 0,
      }}><Icon name={icon} size={16}/></div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--white)' }}>{title}</div>
        {subtitle && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 1 }}>{subtitle}</div>}
      </div>
    </div>
  );
}

function Input({ value, onChange, onEnter, placeholder, disabled }) {
  return (
    <input
      value={value}
      onChange={e => onChange(e.target.value)}
      onKeyDown={e => e.key === 'Enter' && onEnter && onEnter()}
      placeholder={placeholder}
      disabled={disabled}
      style={{
        flex: 1, padding: '9px 13px',
        background: 'var(--input)', border: '0.5px solid var(--border)',
        borderRadius: 8, color: 'var(--white)',
        fontSize: 13, fontFamily: 'var(--mono)',
        outline: 'none',
      }}
    />
  );
}

function Btn({ onClick, loading, children, disabled }) {
  return (
    <button onClick={onClick} disabled={loading || disabled} style={{
      padding: '9px 18px',
      background: 'rgba(53,187,234,.15)',
      border: '0.5px solid rgba(53,187,234,.4)',
      borderRadius: 8, color: 'var(--blue)',
      fontSize: 13, fontWeight: 600,
      cursor: loading || disabled ? 'default' : 'pointer',
      opacity: loading || disabled ? 0.55 : 1,
      fontFamily: 'inherit', whiteSpace: 'nowrap',
      display: 'flex', alignItems: 'center', gap: 6,
      transition: 'all .15s',
    }}>
      {loading ? <Spin/> : children}
    </button>
  );
}

function Spin() {
  return (
    <div style={{
      width: 13, height: 13, borderRadius: '50%',
      border: '2px solid rgba(53,187,234,.3)',
      borderTopColor: 'var(--blue)',
      animation: 'spin .7s linear infinite',
    }}/>
  );
}

function Row({ label, value, mono = false, copyable = false }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(String(value));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  if (!value && value !== 0) return null;
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '8px 0', borderBottom: '0.5px solid var(--border-faint)',
      gap: 12,
    }}>
      <span style={{ fontSize: 12, color: 'var(--muted)', flexShrink: 0 }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{
          fontSize: 12.5, color: 'var(--white)',
          fontFamily: mono ? 'var(--mono)' : 'inherit',
          textAlign: 'right', wordBreak: 'break-all',
        }}>{String(value)}</span>
        {copyable && (
          <button onClick={copy} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: copied ? '#4ade80' : 'var(--muted)',
            padding: 2, flexShrink: 0,
          }}>
            <Icon name={copied ? 'check' : 'copy'} size={13}/>
          </button>
        )}
      </div>
    </div>
  );
}

function Badge({ text, color = 'blue' }) {
  const colors = {
    blue:   { bg: 'rgba(53,187,234,.12)',  border: 'rgba(53,187,234,.3)',  text: '#35BBEA' },
    green:  { bg: 'rgba(74,222,128,.12)',  border: 'rgba(74,222,128,.3)',  text: '#4ade80' },
    red:    { bg: 'rgba(239,68,68,.12)',   border: 'rgba(239,68,68,.3)',   text: '#ef4444' },
    yellow: { bg: 'rgba(251,191,36,.12)',  border: 'rgba(251,191,36,.3)',  text: '#fbbf24' },
  };
  const c = colors[color] || colors.blue;
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 20,
      background: c.bg, border: `0.5px solid ${c.border}`,
      fontSize: 11, fontWeight: 600, color: c.text,
    }}>{text}</span>
  );
}

function ErrorMsg({ msg }) {
  if (!msg) return null;
  return (
    <div style={{
      padding: '10px 14px', margin: '12px 20px',
      background: 'rgba(239,68,68,.08)',
      border: '0.5px solid rgba(239,68,68,.3)',
      borderRadius: 8, fontSize: 12.5, color: '#ef4444',
      display: 'flex', gap: 7, alignItems: 'flex-start',
    }}>
      <Icon name="alert" size={14}/> {msg}
    </div>
  );
}

// ── API CALLS ─────────────────────────────────────────────────────────
async function apiFetch(endpoint, params = {}) {
  const url = new URL(`${API}/${endpoint}/`, window.location.origin);
  Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ── TOOL 1: MY IP ─────────────────────────────────────────────────────
function MyIPTool() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => {
    apiFetch('myip')
      .then(setData)
      .catch(() => setError('Could not detect your IP. Check your connection.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Card>
      <CardHead icon="globe" title="What Is My IP" subtitle="Your public IP address and location"/>
      <div style={{ padding: '16px 20px' }}>
        {loading && <div style={{ display:'flex', gap:8, alignItems:'center', color:'var(--muted)', fontSize:13 }}><Spin/> Detecting your IP…</div>}
        <ErrorMsg msg={error}/>
        {data && (
          <>
            <div style={{
              padding: '14px 16px', marginBottom: 16,
              background: 'rgba(53,187,234,.06)',
              border: '0.5px solid rgba(53,187,234,.2)',
              borderRadius: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>Your IP Address</div>
                <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--mono)', color: 'var(--blue)' }}>
                  {data.ip}
                </div>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:4, alignItems:'flex-end' }}>
                {data.version && <Badge text={`IPv${data.version}`}/>}
                {data.is_vpn && <Badge text="VPN Detected" color="yellow"/>}
                {data.is_proxy && <Badge text="Proxy" color="yellow"/>}
              </div>
            </div>
            <Row label="Country"   value={data.country_name ? `${data.country_flag || ''} ${data.country_name}` : null}/>
            <Row label="Region"    value={data.region}/>
            <Row label="City"      value={data.city}/>
            <Row label="ISP"       value={data.isp}/>
            <Row label="Timezone"  value={data.timezone}/>
            <Row label="Latitude"  value={data.latitude}  mono/>
            <Row label="Longitude" value={data.longitude} mono/>
            <Row label="ASN"       value={data.asn}       mono copyable/>
          </>
        )}
      </div>
    </Card>
  );
}

// ── TOOL 2: IP LOOKUP ─────────────────────────────────────────────────
function IPLookupTool() {
  const [ip, setIp]         = useState('');
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const lookup = async () => {
    if (!ip.trim()) return;
    setLoading(true); setError(''); setData(null);
    try {
      const res = await apiFetch('iplookup', { ip: ip.trim() });
      if (res.error) throw new Error(res.error);
      setData(res);
    } catch (e) { setError(e.message || 'Lookup failed'); }
    finally { setLoading(false); }
  };

  return (
    <Card>
      <CardHead icon="search" title="IP Lookup" subtitle="Look up any IP address for location and details"/>
      <div style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <Input value={ip} onChange={setIp} onEnter={lookup} placeholder="Enter IP address (e.g. 8.8.8.8)" disabled={loading}/>
          <Btn onClick={lookup} loading={loading}>Lookup</Btn>
        </div>
        <ErrorMsg msg={error}/>
        {data && (
          <>
            <Row label="IP"        value={data.ip}          mono copyable/>
            <Row label="Country"   value={data.country_name ? `${data.country_flag||''} ${data.country_name}` : null}/>
            <Row label="Region"    value={data.region}/>
            <Row label="City"      value={data.city}/>
            <Row label="ISP"       value={data.isp}/>
            <Row label="ASN"       value={data.asn}         mono/>
            <Row label="Timezone"  value={data.timezone}/>
            <Row label="Latitude"  value={data.latitude}    mono/>
            <Row label="Longitude" value={data.longitude}   mono/>
            {data.is_vpn !== undefined && (
              <div style={{ marginTop: 10, display:'flex', gap:6, flexWrap:'wrap' }}>
                <Badge text={data.is_vpn ? 'VPN Detected' : 'Not VPN'} color={data.is_vpn ? 'yellow' : 'green'}/>
                <Badge text={data.is_proxy ? 'Proxy Detected' : 'Not Proxy'} color={data.is_proxy ? 'yellow' : 'green'}/>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

// ── TOOL 3: HOSTNAME LOOKUP ───────────────────────────────────────────
function HostnameTool() {
  const [host, setHost]     = useState('');
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const lookup = async () => {
    if (!host.trim()) return;
    setLoading(true); setError(''); setData(null);
    try {
      const res = await apiFetch('hostname', { host: host.trim() });
      if (res.error) throw new Error(res.error);
      setData(res);
    } catch (e) { setError(e.message || 'Lookup failed'); }
    finally { setLoading(false); }
  };

  return (
    <Card>
      <CardHead icon="server" title="Hostname Lookup" subtitle="Resolve hostname to IP or IP to hostname"/>
      <div style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <Input value={host} onChange={setHost} onEnter={lookup} placeholder="Enter hostname or IP (e.g. google.com)" disabled={loading}/>
          <Btn onClick={lookup} loading={loading}>Resolve</Btn>
        </div>
        <ErrorMsg msg={error}/>
        {data && (
          <>
            <Row label="Input"       value={data.input}         mono copyable/>
            <Row label="Hostname"    value={data.hostname}      mono copyable/>
            <Row label="IP Address"  value={data.ip}            mono copyable/>
            <Row label="Aliases"     value={data.aliases?.join(', ')}/>
            <Row label="DNS Server"  value={data.dns_server}    mono/>
          </>
        )}
      </div>
    </Card>
  );
}

// ── TOOL 4: WHOIS ─────────────────────────────────────────────────────
function WhoisTool() {
  const [domain, setDomain] = useState('');
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const lookup = async () => {
    if (!domain.trim()) return;
    setLoading(true); setError(''); setData(null);
    try {
      const res = await apiFetch('whois', { domain: domain.trim() });
      if (res.error) throw new Error(res.error);
      setData(res);
    } catch (e) { setError(e.message || 'WHOIS lookup failed'); }
    finally { setLoading(false); }
  };

  return (
    <Card>
      <CardHead icon="search" title="IP WHOIS Lookup" subtitle="Domain registration and IP ownership information"/>
      <div style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <Input value={domain} onChange={setDomain} onEnter={lookup} placeholder="Enter domain or IP (e.g. google.com)" disabled={loading}/>
          <Btn onClick={lookup} loading={loading}>WHOIS</Btn>
        </div>
        <ErrorMsg msg={error}/>
        {data && (
          <>
            <Row label="Domain"       value={data.domain}          mono copyable/>
            <Row label="Registrar"    value={data.registrar}/>
            <Row label="Created"      value={data.created}/>
            <Row label="Expires"      value={data.expires}/>
            <Row label="Updated"      value={data.updated}/>
            <Row label="Status"       value={data.status}/>
            <Row label="Name Servers" value={data.name_servers?.join(', ')} mono/>
            <Row label="Registrant"   value={data.registrant}/>
            <Row label="Country"      value={data.country}/>
          </>
        )}
      </div>
    </Card>
  );
}

// ── TOOL 5: BLACKLIST CHECK ───────────────────────────────────────────
function BlacklistTool() {
  const [ip, setIp]         = useState('');
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const check = async () => {
    if (!ip.trim()) return;
    setLoading(true); setError(''); setData(null);
    try {
      const res = await apiFetch('blacklist', { ip: ip.trim() });
      if (res.error) throw new Error(res.error);
      setData(res);
    } catch (e) { setError(e.message || 'Check failed'); }
    finally { setLoading(false); }
  };

  const listed = data?.blacklists?.filter(b => b.listed) || [];
  const clean  = data?.blacklists?.filter(b => !b.listed) || [];

  return (
    <Card>
      <CardHead icon="shield" title="Blacklist Check" subtitle="Check if an IP is listed on spam or abuse blacklists"/>
      <div style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <Input value={ip} onChange={setIp} onEnter={check} placeholder="Enter IP address to check" disabled={loading}/>
          <Btn onClick={check} loading={loading}>Check</Btn>
        </div>
        <ErrorMsg msg={error}/>
        {data && (
          <>
            <div style={{ marginBottom: 14, display:'flex', gap:8, alignItems:'center' }}>
              <Badge
                text={listed.length === 0 ? '✓ Clean' : `⚠ Listed on ${listed.length} blacklist${listed.length>1?'s':''}`}
                color={listed.length === 0 ? 'green' : 'red'}
              />
              <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                {data.total_checked} blacklists checked
              </span>
            </div>
            {listed.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#ef4444', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.06em' }}>Listed on</div>
                {listed.map((b, i) => (
                  <div key={i} style={{ padding: '5px 0', borderBottom: '0.5px solid var(--border-faint)', display:'flex', justifyContent:'space-between' }}>
                    <span style={{ fontSize: 12.5, fontFamily: 'var(--mono)', color: 'var(--white)' }}>{b.name}</span>
                    <Badge text="LISTED" color="red"/>
                  </div>
                ))}
              </div>
            )}
            {clean.length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#4ade80', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.06em' }}>Clean on</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {clean.map((b, i) => (
                    <span key={i} style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--muted)' }}>{b.name}</span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

// ── TOOL 6: PORT SCANNER ──────────────────────────────────────────────
function PortScanTool() {
  const [host, setHost]     = useState('');
  const [ports, setPorts]   = useState('80,443,22,21,25,3306,5432,8080');
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const scan = async () => {
    if (!host.trim()) return;
    setLoading(true); setError(''); setData(null);
    try {
      const res = await apiFetch('portscan', { host: host.trim(), ports });
      if (res.error) throw new Error(res.error);
      setData(res);
    } catch (e) { setError(e.message || 'Port scan failed'); }
    finally { setLoading(false); }
  };

  const open   = data?.ports?.filter(p => p.status === 'open')   || [];
  const closed = data?.ports?.filter(p => p.status === 'closed') || [];

  return (
    <Card>
      <CardHead icon="port" title="Port Scanner" subtitle="Scan open ports on any host"/>
      <div style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', flexDirection:'column', gap: 8, marginBottom: 12 }}>
          <Input value={host} onChange={setHost} placeholder="Enter hostname or IP" disabled={loading}/>
          <div style={{ display:'flex', gap:8 }}>
            <Input value={ports} onChange={setPorts} placeholder="Ports (comma-separated)" disabled={loading}/>
            <Btn onClick={scan} loading={loading}>Scan</Btn>
          </div>
        </div>
        <ErrorMsg msg={error}/>
        {data && (
          <>
            <div style={{ marginBottom: 10, display:'flex', gap:8 }}>
              <Badge text={`${open.length} Open`}   color="green"/>
              <Badge text={`${closed.length} Closed`} color="red"/>
            </div>
            {data.ports?.map((p, i) => (
              <div key={i} style={{
                display:'flex', justifyContent:'space-between', alignItems:'center',
                padding: '6px 0', borderBottom: '0.5px solid var(--border-faint)',
              }}>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <span style={{ fontFamily:'var(--mono)', fontSize:13, color:'var(--white)', minWidth:45 }}>{p.port}</span>
                  <span style={{ fontSize:12, color:'var(--muted)' }}>{p.service || ''}</span>
                </div>
                <Badge text={p.status} color={p.status === 'open' ? 'green' : 'red'}/>
              </div>
            ))}
          </>
        )}
      </div>
    </Card>
  );
}

// ── TOOL 7: TRACEROUTE ────────────────────────────────────────────────
function TracerouteTool() {
  const [host, setHost]       = useState('');
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const trace = async () => {
    if (!host.trim()) return;
    setLoading(true);
    setError('');
    setData(null);

    try {
      // Call hackertarget directly from the browser — bypasses server restrictions
      const res = await fetch(
        `https://api.hackertarget.com/traceroute/?q=${encodeURIComponent(host.trim())}`,
        { signal: AbortSignal.timeout(30000) }
      );
      const text = await res.text();

      if (text.includes('API count') || text.includes('limit')) {
        throw new Error('Daily API limit reached (100/day). Try again tomorrow.');
      }
      if (text.includes('error') || text.includes('invalid')) {
        throw new Error('Could not trace route to that host.');
      }

      // Parse the plain text response
      // Format: "1  router (192.168.1.1)  1.234 ms"
      const lines = text.trim().split('\n');
      const hops  = [];

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;

        const parts = trimmed.split(/\s+/);
        let hopNum = null;

        try { hopNum = parseInt(parts[0]); } catch { continue; }
        if (isNaN(hopNum)) continue;

        let rtt      = null;
        let hopHost  = null;
        let hopIp    = null;

        for (let i = 1; i < parts.length; i++) {
          const p = parts[i];

          // RTT value like "12.345"
          if (!isNaN(parseFloat(p)) && !p.includes('.') === false) {
            rtt = parseFloat(p);
            continue;
          }
          // ms unit — rtt is the previous token
          if (p === 'ms' && i > 1) {
            rtt = parseFloat(parts[i - 1]);
            continue;
          }
          // IP in parens like (8.8.8.8)
          if (p.startsWith('(') && p.endsWith(')')) {
            hopIp = p.slice(1, -1);
            continue;
          }
          // Plain IP address
          if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(p)) {
            hopIp = p;
            continue;
          }
          // Hostname — has dots but not an IP
          if (p.includes('.') && !/^\d/.test(p) && p !== 'ms') {
            hopHost = p;
          }
        }

        // * means no response
        const isTimeout = parts.includes('*') && !hopIp;

        hops.push({
          hop:     hopNum,
          ip:      hopIp,
          host:    hopHost || hopIp,
          rtt:     rtt,
          timeout: isTimeout,
        });
      }

      if (hops.length === 0) {
        throw new Error('No hops returned. The host may be unreachable.');
      }

      setData({ host: host.trim(), hops, total_hops: hops.length });

    } catch (e) {
      setError(e.message || 'Traceroute failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHead
        icon="route"
        title="Traceroute"
        subtitle="Trace the network path to any host"
      />
      <div style={{ padding:'16px 20px' }}>
        <div style={{ display:'flex', gap:8, marginBottom:12 }}>
          <Input
            value={host}
            onChange={setHost}
            onEnter={trace}
            placeholder="Enter hostname or IP (e.g. google.com)"
            disabled={loading}
          />
          <Btn onClick={trace} loading={loading}>Trace</Btn>
        </div>

        {loading && (
          <div style={{
            fontSize:12.5, color:'var(--muted)', marginBottom:8,
            display:'flex', alignItems:'center', gap:6,
          }}>
            <Spin/> Tracing route — this takes 15–30 seconds…
          </div>
        )}

        <ErrorMsg msg={error}/>

        {data && (
          <>
            <div style={{
              fontSize:11.5, color:'var(--muted)', marginBottom:12
            }}>
              {data.total_hops} hops to <strong style={{ color:'var(--white)' }}>{data.host}</strong>
            </div>

            {/* hop table header */}
            <div style={{
              display:'grid',
              gridTemplateColumns:'32px 80px 1fr 70px',
              gap:10, padding:'4px 0 8px',
              borderBottom:'0.5px solid var(--border)',
              fontSize:10.5, fontWeight:600,
              textTransform:'uppercase', letterSpacing:'.06em',
              color:'var(--muted)',
            }}>
              <span>#</span>
              <span>RTT</span>
              <span>Host</span>
              <span>Status</span>
            </div>

            {/* hop rows */}
            {data.hops.map((hop, i) => (
              <div key={i} style={{
                display:'grid',
                gridTemplateColumns:'32px 80px 1fr 70px',
                gap:10, padding:'7px 0',
                borderBottom:'0.5px solid var(--border-faint)',
                alignItems:'center',
              }}>
                <span style={{
                  fontSize:12, fontFamily:'var(--mono)',
                  color:'var(--muted)',
                }}>
                  {hop.hop}
                </span>
                <span style={{
                  fontSize:12, fontFamily:'var(--mono)',
                  color: hop.timeout ? 'var(--muted)'
                       : hop.rtt < 20  ? '#4ade80'
                       : hop.rtt < 80  ? '#fbbf24'
                       : '#ef4444',
                }}>
                  {hop.timeout ? '*' : hop.rtt ? `${hop.rtt}ms` : '*'}
                </span>
                <span style={{
                  fontSize:12, color: hop.timeout ? 'var(--muted)' : 'var(--white)',
                  overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                }}>
                  {hop.timeout ? 'Request timed out' : (hop.host || hop.ip || '*')}
                </span>
                <Badge
                  text={hop.timeout ? 'timeout' : 'ok'}
                  color={hop.timeout ? 'yellow' : 'green'}
                />
              </div>
            ))}

            {/* summary */}
            <div style={{
              marginTop:12, padding:'8px 12px',
              background:'rgba(53,187,234,.06)',
              border:'0.5px solid rgba(53,187,234,.2)',
              borderRadius:8, fontSize:11.5, color:'var(--muted)',
            }}>
              Total hops: {data.total_hops} ·
              Avg RTT: {
                (() => {
                  const rtts = data.hops.filter(h => h.rtt).map(h => h.rtt);
                  return rtts.length
                    ? `${(rtts.reduce((a,b) => a+b, 0) / rtts.length).toFixed(1)}ms`
                    : 'n/a';
                })()
              } ·
              Timeouts: {data.hops.filter(h => h.timeout).length}
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

// ── TOOL 8: DNS LOOKUP ────────────────────────────────────────────────
function DNSTool() {
  const [domain, setDomain] = useState('');
  const [type, setType]     = useState('A');
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const lookup = async () => {
    if (!domain.trim()) return;
    setLoading(true); setError(''); setData(null);
    try {
      const res = await apiFetch('dns', { domain: domain.trim(), type });
      if (res.error) throw new Error(res.error);
      setData(res);
    } catch (e) { setError(e.message || 'DNS lookup failed'); }
    finally { setLoading(false); }
  };

  return (
    <Card>
      <CardHead icon="wifi" title="DNS Lookup" subtitle="Query DNS records for any domain"/>
      <div style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap:'wrap' }}>
          <Input value={domain} onChange={setDomain} onEnter={lookup} placeholder="Enter domain (e.g. google.com)" disabled={loading}/>
          <select value={type} onChange={e => setType(e.target.value)} style={{
            padding:'9px 10px', background:'var(--input)',
            border:'0.5px solid var(--border)', borderRadius:8,
            color:'var(--white)', fontSize:13, fontFamily:'var(--mono)',
          }}>
            {['A','AAAA','MX','TXT','NS','CNAME','SOA','PTR'].map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <Btn onClick={lookup} loading={loading}>Query</Btn>
        </div>
        <ErrorMsg msg={error}/>
        {data && (
          <div>
            <div style={{ fontSize:11, color:'var(--muted)', marginBottom:8 }}>
              {data.records?.length} {type} record{data.records?.length !== 1 ? 's' : ''} for {data.domain}
            </div>
            {data.records?.map((r, i) => (
              <div key={i} style={{
                padding:'7px 10px', marginBottom:5,
                background:'var(--surface2)', borderRadius:7,
                fontFamily:'var(--mono)', fontSize:12,
                color:'var(--white)', wordBreak:'break-all',
                border:'0.5px solid var(--border)',
              }}>
                {typeof r === 'object' ? JSON.stringify(r) : r}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

// ── TOOL 9: USER AGENT ────────────────────────────────────────────────
function UserAgentTool() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => {
    apiFetch('useragent')
      .then(setData)
      .catch(() => setError('Could not parse user agent.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Card>
      <CardHead icon="server" title="User Agent Info" subtitle="Details about your browser and device"/>
      <div style={{ padding: '16px 20px' }}>
        {loading && <div style={{ display:'flex', gap:8, alignItems:'center', color:'var(--muted)', fontSize:13 }}><Spin/> Detecting…</div>}
        <ErrorMsg msg={error}/>
        {data && (
          <>
            <div style={{
              padding:'10px 12px', marginBottom:14,
              background:'var(--surface2)', borderRadius:8,
              fontFamily:'var(--mono)', fontSize:11.5, color:'var(--muted)',
              wordBreak:'break-all', lineHeight:1.6,
              border:'0.5px solid var(--border)',
            }}>{data.raw}</div>
            <Row label="Browser"  value={data.browser}/>
            <Row label="Version"  value={data.version}/>
            <Row label="OS"       value={data.os}/>
            <Row label="Device"   value={data.device}/>
            <Row label="Engine"   value={data.engine}/>
            <Row label="Mobile"   value={data.is_mobile ? 'Yes' : 'No'}/>
            <Row label="Bot"      value={data.is_bot    ? 'Yes' : 'No'}/>
          </>
        )}
      </div>
    </Card>
  );
}

// ── TOOL 10: SPEED TEST ───────────────────────────────────────────────
function SpeedTestTool() {
  const [phase,   setPhase]   = useState('idle');
  const [results, setResults] = useState({});
  const [log,     setLog]     = useState([]);
  const [error,   setError]   = useState('');

  const addLog = (msg) => setLog(prev => [...prev, msg]);

  // ── Ping — 10 samples, take median ──────────────────────────────
  const measurePing = async () => {
    setPhase('ping');
    addLog('📡 Measuring ping (10 samples)…');
    const samples = [];
    for (let i = 0; i < 10; i++) {
      const t0  = performance.now();
      await fetch(`http://localhost:8000/api/tools/ping/?t=${Date.now()}`, { cache: 'no-store' });
      const rtt = performance.now() - t0;
      samples.push(rtt);
    }
    // Sort and take median
    samples.sort((a, b) => a - b);
    const median = samples[Math.floor(samples.length / 2)];
    const ping   = Math.round(median);
    setResults(r => ({ ...r, ping }));
    addLog(`  → Ping: ${ping}ms (median of 10 samples)`);
    return ping;
  };

  // ── Download — 3 parallel connections, 25MB each ────────────────
  const measureDownload = async () => {
    setPhase('download');
    addLog('⬇ Measuring download (3 parallel connections, 25MB each)…');

    const SIZE        = 25_000_000;   // 25MB per connection
    const CONNECTIONS = 3;
    const start       = performance.now();
    let   totalBytes  = 0;

    const fetchOne = async () => {
      const res    = await fetch(
        `http://localhost:8000/api/tools/speedtest/?size=${SIZE}&t=${Date.now()}`,
        { cache: 'no-store' }
      );
      const buffer = await res.arrayBuffer();
      return buffer.byteLength;
    };

    try {
      const results = await Promise.all(
        Array.from({ length: CONNECTIONS }, fetchOne)
      );
      totalBytes = results.reduce((a, b) => a + b, 0);
    } catch (e) {
      throw new Error(`Download failed: ${e.message}`);
    }

    const elapsed  = (performance.now() - start) / 1000;  // seconds
    const mbps     = parseFloat(((totalBytes * 8) / elapsed / 1_000_000).toFixed(1));
    setResults(r => ({ ...r, download: mbps }));
    addLog(`  → Download: ${mbps} Mbps (${(totalBytes/1_000_000).toFixed(0)}MB in ${elapsed.toFixed(1)}s)`);
    return mbps;
  };

  // ── Upload — send 10MB to server, measure throughput ────────────
  const measureUpload = async () => {
    setPhase('upload');
    addLog('⬆ Measuring upload (10MB)…');

    const SIZE  = 10_000_000;   // 10MB
    // Random data so it can't be compressed
    const data  = new Uint8Array(SIZE);
    crypto.getRandomValues(data.subarray(0, Math.min(SIZE, 65536)));
    // Fill rest with pattern (crypto.getRandomValues has size limit)
    for (let i = 65536; i < SIZE; i++) data[i] = i % 256;
    const blob  = new Blob([data], { type: 'application/octet-stream' });

    const start = performance.now();
    try {
      const res = await fetch('http://localhost:8000/api/tools/speedtest/upload/', {
        method:  'POST',
        body:    blob,
        headers: { 'Content-Type': 'application/octet-stream' },
        cache:   'no-store',
      });
      await res.json();
    } catch (e) {
      throw new Error(`Upload failed: ${e.message}`);
    }

    const elapsed = (performance.now() - start) / 1000;
    const mbps    = parseFloat(((SIZE * 8) / elapsed / 1_000_000).toFixed(1));
    setResults(r => ({ ...r, upload: mbps }));
    addLog(`  → Upload: ${mbps} Mbps (${(SIZE/1_000_000).toFixed(0)}MB in ${elapsed.toFixed(1)}s)`);
    return mbps;
  };

  // ── Run full test ────────────────────────────────────────────────
  const runTest = async () => {
    setPhase('running');
    setResults({});
    setLog([]);
    setError('');
    addLog('Starting speed test…');

    try {
      await measurePing();
      await measureDownload();
      await measureUpload();
      setPhase('done');
      addLog('✅ Test complete');
    } catch (e) {
      setError(e.message);
      setPhase('error');
    }
  };

  // ── Speed rating ─────────────────────────────────────────────────
  const rateSpeed = (mbps, type) => {
    if (type === 'ping') {
      if (mbps < 20)  return { label: 'Excellent', color: '#4ade80' };
      if (mbps < 50)  return { label: 'Good',      color: '#4ade80' };
      if (mbps < 100) return { label: 'Fair',       color: '#fbbf24' };
      return               { label: 'Poor',         color: '#ef4444' };
    }
    if (mbps > 100) return { label: 'Excellent', color: '#4ade80' };
    if (mbps > 25)  return { label: 'Good',      color: '#4ade80' };
    if (mbps > 5)   return { label: 'Fair',       color: '#fbbf24' };
    return               { label: 'Slow',         color: '#ef4444' };
  };

  const metrics = [
    { key: 'ping',     label: 'Ping',     unit: 'ms',   icon: '📡' },
    { key: 'download', label: 'Download', unit: 'Mbps', icon: '⬇' },
    { key: 'upload',   label: 'Upload',   unit: 'Mbps', icon: '⬆' },
  ];

  return (
    <Card>
      <CardHead icon="zap" title="Speed Test"
        subtitle="Tests connection between your browser and this server"/>
      <div style={{ padding: '16px 20px' }}>

        {/* Start button */}
        {phase === 'idle' && (
          <Btn onClick={runTest}>▶ Start Speed Test</Btn>
        )}

        {/* Progress bar */}
        {phase === 'running' || phase === 'ping' ||
         phase === 'download' || phase === 'upload' ? (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize:13, color:'var(--muted)', marginBottom:8 }}>
              {phase === 'ping'     && '📡 Testing ping…'}
              {phase === 'download' && '⬇ Testing download…'}
              {phase === 'upload'   && '⬆ Testing upload…'}
              {phase === 'running'  && '⏳ Starting…'}
            </div>
            <div style={{
              height: 4, background:'var(--border)', borderRadius:2, overflow:'hidden',
            }}>
              <div style={{
                height: '100%', borderRadius:2,
                background: 'linear-gradient(90deg, #35BBEA, #3b82f6)',
                width: phase==='ping' ? '33%' : phase==='download' ? '66%' : '90%',
                transition: 'width .5s ease',
              }}/>
            </div>
          </div>
        ) : null}

        {/* Error */}
        {error && <ErrorMsg msg={error}/>}

        {/* Results */}
        {Object.keys(results).length > 0 && (
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
            gap: 10, marginBottom: 14,
          }}>
            {metrics.map(({ key, label, unit, icon }) => {
              const val  = results[key];
              const rate = val !== undefined
                ? rateSpeed(val, key)
                : null;
              return (
                <div key={key} style={{
                  padding: '12px 8px', textAlign: 'center',
                  background: 'var(--surface2)', borderRadius: 10,
                  border: `.5px solid ${rate ? rate.color + '44' : 'var(--border)'}`,
                }}>
                  <div style={{ fontSize:11, color:'var(--muted)', marginBottom:4 }}>
                    {icon} {label}
                  </div>
                  <div style={{
                    fontSize: 20, fontWeight: 700, fontFamily: 'var(--mono)',
                    color: rate ? rate.color : 'var(--muted)',
                  }}>
                    {val !== undefined ? val : <Spin/>}
                  </div>
                  <div style={{ fontSize: 10, color:'var(--muted)' }}>{unit}</div>
                  {rate && (
                    <div style={{ fontSize: 10, color: rate.color, marginTop: 3 }}>
                      {rate.label}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Log */}
        {log.length > 0 && (
          <div style={{
            background: 'var(--surface2)', border: '.5px solid var(--border)',
            borderRadius: 8, padding: '8px 10px', marginBottom: 12,
          }}>
            {log.map((line, i) => (
              <div key={i} style={{
                fontSize: 11.5, fontFamily: 'var(--mono)',
                color: 'var(--muted)', lineHeight: 1.7,
              }}>{line}</div>
            ))}
          </div>
        )}

        {/* Re-run */}
        {phase === 'done' && (
          <Btn onClick={runTest}>↺ Run Again</Btn>
        )}

        {/* Note */}
        <div style={{
          marginTop: 10, fontSize: 11, color: 'var(--muted)', lineHeight: 1.6,
        }}>
          ⚡ Measures speed between your browser and this server.
          Results reflect your connection to arhatinfo.com specifically.
        </div>
      </div>
    </Card>
  );
}
// ── TABS ──────────────────────────────────────────────────────────────
const TOOLS = [
  { id:'myip',      label:'My IP',        icon:'globe',  component: MyIPTool      },
  { id:'iplookup',  label:'IP Lookup',    icon:'search', component: IPLookupTool  },
  { id:'hostname',  label:'Hostname',     icon:'server', component: HostnameTool  },
  { id:'whois',     label:'WHOIS',        icon:'search', component: WhoisTool     },
  { id:'blacklist', label:'Blacklist',    icon:'shield', component: BlacklistTool },
  { id:'ports',     label:'Port Scan',   icon:'port',   component: PortScanTool  },
  // { id:'traceroute',label:'Traceroute',  icon:'route',  component: TracerouteTool},
  { id:'dns',       label:'DNS Lookup',  icon:'wifi',   component: DNSTool       },
  { id:'useragent', label:'User Agent',  icon:'server', component: UserAgentTool },
  { id:'speed',     label:'Speed Test',  icon:'zap',    component: SpeedTestTool },
];

// ── MAIN PAGE ─────────────────────────────────────────────────────────
export default function IPTools() {
    const navigate = useNavigate();

  const hostname = window.location.hostname;

// app.dev.example.com => ["app", "dev", "example", "com"]
const parts = hostname.split(".");

const firstSubdomain =
  parts.length > 2 ? parts[0] : null;

console.log(firstSubdomain);

const path = window.location.pathname;

// "/tools/useragent/"
const match = path.match(/^\/tools\/([^/]+)/);

const toolName = match ? match[1] : null;

console.log(toolName);

  const [active, setActive] = useState(toolName?toolName:firstSubdomain ? firstSubdomain : 'myip');
  const ActiveTool = TOOLS.find(t => t.id === active)?.component || MyIPTool;

  return (
    <>
      <style>{`
        :root {
          --blue: #35BBEA;
          --surface: rgba(255,255,255,.04);
          --surface2: rgba(255,255,255,.02);
          --border: rgba(255,255,255,.09);
          --border-faint: rgba(255,255,255,.05);
          --white: rgba(235,242,255,.92);
          --muted: rgba(255,255,255,.38);
          --input: rgba(255,255,255,.05);
          --mono: 'JetBrains Mono', 'Fira Code', monospace;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        select option { background: #0d1421; color: white; }
      `}</style>
 <div className="page">
    <section className="section" style={{ paddingTop: 0 }}>
      <div style={{ padding: '32px 0' }}>
        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{
            fontSize: 'clamp(1.6rem, 3vw, 2.2rem)',
            fontWeight: 900, color: 'var(--white)',
            letterSpacing: '-.02em', marginBottom: 6,
          }}>
            🌐 IP Tools
          </h1>
          <p style={{ fontSize: 14, color: 'var(--muted)' }}>
            Network utilities — IP lookup, WHOIS, port scan, DNS, traceroute and more
          </p>
        </div>

        {/* Tool tabs */}
        <div style={{
          display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20,
        }}>
          {TOOLS.map(t => (
            <button key={t.id} onClick={() => {navigate(`/tools/ip/${t.id}/`)
            setActive(t.id);
          }} style={{
              padding: '7px 13px',
              border: `0.5px solid ${active === t.id ? 'rgba(53,187,234,.5)' : 'rgba(255,255,255,.1)'}`,
              borderRadius: 8,
              background: active === t.id ? 'rgba(53,187,234,.12)' : 'rgba(255,255,255,.03)',
              color: active === t.id ? 'var(--blue)' : 'var(--muted)',
              fontSize: 12.5, fontWeight: active === t.id ? 600 : 400,
              cursor: 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', gap: 5,
              transition: 'all .15s',
            }}>
              <Icon name={t.icon} size={13}/>
              {t.label}
            </button>
          ))}
        </div>

        {/* Active tool */}
        <ActiveTool/>
      
      </div>
      
      </section></div>
      
    </>
  );
}
